import { combineReducers, configureStore } from "@reduxjs/toolkit";
import {
  persistReducer,
  persistStore,
  createTransform,
  type PersistConfig,
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
} from "redux-persist";
import storage from "redux-persist/lib/storage";

import { STORAGE_KEYS } from "@/constants";
import {
  getStoredAccessToken,
  getStoredRefreshToken,
  setStoredAccessToken,
  setStoredRefreshToken,
} from "@/utils/auth-storage";
import type { AuthState } from "@/types";

import appReducer from "./app.slice";
import authReducer from "./auth.slice";
import cartReducer from "./cart.slice";

const rootReducer = combineReducers({
  app: appReducer,
  auth: authReducer,
  cart: cartReducer,
});

export type RootState = ReturnType<typeof rootReducer>;

type PersistedAuthState = Pick<
  AuthState,
  | "user"
  | "tenant"
  | "accessToken"
  | "refreshToken"
  | "status"
  | "isAuthenticated"
>;

const authPersistTransform = createTransform<AuthState, PersistedAuthState>(
  (state) => ({
    user: state.user,
    tenant: state.tenant,
    accessToken: state.accessToken,
    refreshToken: state.refreshToken,
    status: state.status,
    isAuthenticated: state.isAuthenticated,
  }),
  (state) => ({
    ...state,
    showAccountLocked: false,
    sessionExpiredNoticeId: 0,
  }),
  {
    whitelist: ["auth"],
  },
);

const persistConfig: PersistConfig<RootState> = {
  key: STORAGE_KEYS.PERSIST_ROOT,
  storage,
  whitelist: ["auth", "cart"],
  transforms: [authPersistTransform],
};

const persistedReducer = persistReducer<RootState>(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

let currentAccessToken = getStoredAccessToken();
let currentRefreshToken = getStoredRefreshToken();

store.subscribe(() => {
  const nextState = (store.getState() as RootState).auth;
  const nextAccessToken = nextState.accessToken;
  if (nextAccessToken !== currentAccessToken) {
    currentAccessToken = nextAccessToken;
    setStoredAccessToken(nextAccessToken);
  }

  const nextRefreshToken = nextState.refreshToken;
  if (nextRefreshToken !== currentRefreshToken) {
    currentRefreshToken = nextRefreshToken;
    setStoredRefreshToken(nextRefreshToken);
  }
});

export const persistor = persistStore(store);

export type AppDispatch = typeof store.dispatch;
